// src/components/map/MarkerManager.tsx
import React, { useCallback, useMemo } from 'react';
import { Marker, InfoWindow } from '@react-google-maps/api';
import { AddressStatus } from '../../types/address.types';

export interface MarkerData {
  id: string;
  position: { lat: number; lng: number };
  status?: AddressStatus;
  title?: string;
  label?: string;
  data?: any;
}

interface MarkerManagerProps {
  markers: MarkerData[];
  selectedMarkerId?: string | null;
  onMarkerClick?: (marker: MarkerData) => void;
  onMarkerClose?: () => void;
  renderInfoWindow?: (marker: MarkerData) => React.ReactNode;
  showLabels?: boolean;
}

// Status-based marker colors
const STATUS_COLORS: Record<AddressStatus, string> = {
  pending: '#9CA3AF',      // Gray
  visited: '#3B82F6',      // Blue
  not_home: '#F59E0B',     // Yellow/Amber
  refused: '#EF4444',      // Red
  completed: '#10B981',    // Green
  inaccessible: '#8B5CF6', // Purple
};

const DEFAULT_COLOR = '#6B7280'; // Gray for unknown status

const MarkerManager: React.FC<MarkerManagerProps> = ({
  markers,
  selectedMarkerId,
  onMarkerClick,
  onMarkerClose,
  renderInfoWindow,
  showLabels = false,
}) => {
  const selectedMarker = useMemo(() =>
    markers.find(m => m.id === selectedMarkerId),
    [markers, selectedMarkerId]
  );

  const getMarkerIcon = useCallback((status?: AddressStatus): google.maps.Symbol => {
    const color = status ? STATUS_COLORS[status] : DEFAULT_COLOR;
    return {
      path: google.maps.SymbolPath.CIRCLE,
      fillColor: color,
      fillOpacity: 1,
      strokeColor: '#FFFFFF',
      strokeWeight: 2,
      scale: 10,
    };
  }, []);

  const handleMarkerClick = useCallback((marker: MarkerData) => {
    onMarkerClick?.(marker);
  }, [onMarkerClick]);

  return (
    <>
      {markers.map((marker) => (
        <Marker
          key={marker.id}
          position={marker.position}
          title={marker.title}
          label={showLabels && marker.label ? {
            text: marker.label,
            color: '#FFFFFF',
            fontSize: '10px',
            fontWeight: 'bold',
          } : undefined}
          icon={getMarkerIcon(marker.status)}
          onClick={() => handleMarkerClick(marker)}
        />
      ))}

      {selectedMarker && renderInfoWindow && (
        <InfoWindow
          position={selectedMarker.position}
          onCloseClick={onMarkerClose}
        >
          <div className="min-w-[200px]">
            {renderInfoWindow(selectedMarker)}
          </div>
        </InfoWindow>
      )}
    </>
  );
};

export default MarkerManager;

// Status Legend Component
export const StatusLegend: React.FC<{ className?: string }> = ({ className = '' }) => {
  const statuses: { status: AddressStatus; label: string }[] = [
    { status: 'pending', label: 'Pending' },
    { status: 'visited', label: 'Visited' },
    { status: 'not_home', label: 'Not Home' },
    { status: 'refused', label: 'Refused' },
    { status: 'completed', label: 'Completed' },
    { status: 'inaccessible', label: 'Inaccessible' },
  ];

  return (
    <div className={`bg-white rounded-lg shadow-md p-3 ${className}`}>
      <h4 className="text-sm font-semibold text-gray-700 mb-2">Status Legend</h4>
      <div className="grid grid-cols-2 gap-2">
        {statuses.map(({ status, label }) => (
          <div key={status} className="flex items-center">
            <div
              className="w-3 h-3 rounded-full mr-2 border border-white"
              style={{ backgroundColor: STATUS_COLORS[status] }}
            />
            <span className="text-xs text-gray-600">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
